import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AssignExposureTemplateRequest,
  CreateExposureTemplateRequest,
  ExposureBusinessCandidate,
  ExposureTemplateAssignment,
  ExposureTemplateDetail,
  ExposureTemplateStatus,
  ExposureTemplateSummary,
  SaveExposureTemplateRequest,
} from '../exposure-register.models';

/**
 * Exposure assessment templates — FlexiExposureTemplatesController. Admin
 * only, scoped by the backend to the caller's application. Same v1 prefix as
 * the business routes.
 */
@Injectable({ providedIn: 'root' })
export class FlexiExposureTemplateService {
  private readonly base = `${environment.BASE_URL}/flexi/exposure-templates`;

  constructor(private http: HttpClient) {}

  list(status: ExposureTemplateStatus | null = null): Observable<ExposureTemplateSummary[]> {
    let params = new HttpParams();
    if (status !== null) {
      params = params.set('status', status);
    }
    return this.http
      .get<ExposureTemplateSummary[] | null>(this.base, { params })
      .pipe(map(rows => rows ?? []));
  }

  get(id: number): Observable<ExposureTemplateDetail> {
    return this.http.get<ExposureTemplateDetail>(`${this.base}/${id}`);
  }

  /** Creates a draft and returns its id. */
  create(request: CreateExposureTemplateRequest): Observable<number> {
    return this.http
      .post<number | { Id: number }>(this.base, request)
      .pipe(map(result => Number(typeof result === 'number' ? result : result?.Id)));
  }

  /** Replaces metadata and every factor. 409 when the template is archived. */
  save(id: number, request: SaveExposureTemplateRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/${id}`, request);
  }

  /** Version + 1 and Published. 400 when the template has no factors. */
  publish(id: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/publish`, {});
  }

  /** Archived, which also clears IsDefaultForNewBusinesses. */
  archive(id: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/archive`, {});
  }

  /** 409 unless Published. */
  setDefaultForNew(id: number, isDefault: boolean): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/default-for-new`, { IsDefault: isDefault });
  }

  businesses(id: number): Observable<ExposureTemplateAssignment[]> {
    return this.http
      .get<ExposureTemplateAssignment[] | null>(`${this.base}/${id}/businesses`)
      .pipe(map(rows => rows ?? []));
  }

  /** At most 50 active businesses of the application, matching the search. */
  businessCandidates(id: number, search: string): Observable<ExposureBusinessCandidate[]> {
    const params = new HttpParams().set('search', search);
    return this.http
      .get<ExposureBusinessCandidate[] | null>(`${this.base}/${id}/business-candidates`, { params })
      .pipe(map(rows => rows ?? []));
  }

  /** 409 unless Published. */
  assign(id: number, request: AssignExposureTemplateRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/businesses`, request);
  }

  unassign(id: number, businessId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/businesses/${businessId}`);
  }
}
